export const INFRA_FILTER_FIELDS = [
  'cidade_nome',
  'assunto',
  'estrutura_referencia_normalizada',
  'funcionario',
  'ano_mes_abertura',
  'ano_mes_fechamento',
  'estrutura_descricao',
  'projeto_nome',
];

export const INFRA_HISTORY_FILTER_FIELDS = [
  'cidade_nome',
  'assunto',
  'funcionario',
  'estrutura_referencia_normalizada',
  'ano_mes_abertura',
  'ano_mes_fechamento',
  'estrutura_descricao',
  'projeto_nome',
];

export const FIELD_LABELS = {
  cidade_nome: 'Cidade',
  assunto: 'Assunto',
  estrutura_referencia_normalizada: 'Referência',
  funcionario: 'Colaborador',
  ano_mes_abertura: 'Mês de abertura',
  ano_mes_fechamento: 'Mês de fechamento',
  estrutura_descricao: 'Estrutura',
  projeto_nome: 'Projeto',
};

export const normalizeText = (value) => String(value ?? '').trim().toLocaleLowerCase('pt-BR');

export const normalizeDisplayValue = (value) => {
  const text = String(value ?? '').trim();
  return text === '' ? 'Não informado' : text;
};

export const formatInfraMonth = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return 'Não informado';

  const match = text.match(/^(\d{4})-(\d{2})$/);
  if (!match) {
    return text;
  }

  const [, year, month] = match;
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) {
    return text;
  }

  return date.toLocaleDateString('pt-BR', {
    month: '2-digit',
    year: 'numeric',
  });
};

export const formatInfraDateTime = (value, { withTime = true } = {}) => {
  const text = String(value ?? '').trim();
  if (!text) return 'Não informado';

  const normalized = text.replace(' ', 'T');
  const parsed = new Date(normalized);

  if (Number.isNaN(parsed.getTime())) {
    return text;
  }

  return parsed.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour12: false,
    ...(withTime
      ? {
        hour: '2-digit',
        minute: '2-digit',
      }
      : {}),
  });
};

export const parseInfraDateTimeValue = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return null;

  const normalized = text.replace(' ', 'T');
  const parsed = new Date(normalized);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
};

export const formatInfraDurationHours = (value, { compact = false } = {}) => {
  const hoursValue = normalizeNumber(value);

  if (!Number.isFinite(hoursValue) || hoursValue <= 0) {
    return compact ? '0min' : '0 min';
  }

  const totalMinutes = Math.round(hoursValue * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) {
    return compact ? `${minutes}min` : `${minutes} min`;
  }

  if (minutes <= 0) {
    return compact ? `${hours}h` : `${hours}h`;
  }

  return compact ? `${hours}h${minutes}min` : `${hours}h ${minutes}min`;
};

export const getInfraDurationHours = (row, startField, endField) => {
  const start = parseInfraDateTimeValue(row?.[startField]);
  const end = parseInfraDateTimeValue(row?.[endField]);

  if (!start || !end) {
    return null;
  }

  const diffMs = end.getTime() - start.getTime();

  if (!Number.isFinite(diffMs) || diffMs < 0) {
    return null;
  }

  return diffMs / (1000 * 60 * 60);
};

export const calculateInfraAverageDurationHours = (rows, startField, endField) => {
  const durations = (Array.isArray(rows) ? rows : [])
    .map((row) => getInfraDurationHours(row, startField, endField))
    .filter((value) => Number.isFinite(value) && value >= 0);

  if (durations.length === 0) {
    return 0;
  }

  return durations.reduce((acc, value) => acc + value, 0) / durations.length;
};

export const normalizeFilterValue = (value) => {
  const text = String(value ?? '').trim();
  return text === '' ? null : value;
};

export const normalizeFlag = (value) => {
  if (value === true || value === 1 || value === '1') return 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', 'sim', 'yes'].includes(normalized)) return 1;
  }
  return 0;
};

export const isInfraOpenedInPeriod = (row) => normalizeFlag(row?.aberta_no_periodo) === 1;

export const isInfraClosedInPeriod = (row) => normalizeFlag(row?.fechada_no_periodo) === 1;

export const getInfraEventType = (row) => {
  const opened = isInfraOpenedInPeriod(row);
  const closed = isInfraClosedInPeriod(row);

  if (opened && closed) return 'Ambas';
  if (opened) return 'Aberta';
  if (closed) return 'Fechada';
  return 'Não informado';
};

export const getInfraHistoryMonth = (row, field = 'ano_mes_fechamento') => {
  const rawValue = field === 'ano_mes_abertura'
    ? row?.ano_mes_abertura
    : row?.ano_mes_fechamento;
  return normalizeDisplayValue(rawValue);
};

export const getEligibleHistoryFilters = (filters) => (
  (Array.isArray(filters) ? filters : [])
    .filter((filter) => filter?.field && INFRA_HISTORY_FILTER_FIELDS.includes(filter.field))
    .map((filter) => ({
      field: filter.field,
      value: normalizeFilterValue(filter.value),
      label: filter.label || buildFilterLabel(filter.field, normalizeFilterValue(filter.value)),
    }))
);

export const serializeHistoryFilters = (filters) => {
  const eligible = getEligibleHistoryFilters(filters);
  const grouped = eligible.reduce((acc, filter) => {
    if (!acc[filter.field]) {
      acc[filter.field] = new Map();
    }

    const key = filter.value === null ? '__NULL__' : normalizeText(filter.value);
    acc[filter.field].set(key, filter.value);
    return acc;
  }, {});

  return Object.keys(grouped)
    .sort((a, b) => a.localeCompare(b, 'pt-BR'))
    .flatMap((field) => {
      const values = Array.from(grouped[field].values()).sort((left, right) => {
        if (left === null && right === null) return 0;
        if (left === null) return -1;
        if (right === null) return 1;
        return String(left).localeCompare(String(right), 'pt-BR', { sensitivity: 'base' });
      });

      return values.map((value) => ({
        field,
        value,
        label: buildFilterLabel(field, value),
      }));
    });
};

export const normalizeNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value !== 'string') {
    return 0;
  }

  const raw = value.trim();
  if (!raw) return 0;

  const cleaned = raw.replace(/[^\d,.-]/g, '');
  if (!cleaned) return 0;

  const hasDot = cleaned.includes('.');
  const hasComma = cleaned.includes(',');

  if (hasDot && hasComma) {
    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    if (lastComma > lastDot) {
      return Number.parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
    }
    return Number.parseFloat(cleaned.replace(/,/g, '')) || 0;
  }

  if (hasComma) {
    return Number.parseFloat(cleaned.replace(',', '.')) || 0;
  }

  return Number.parseFloat(cleaned) || 0;
};

export const getRowValue = (row, field) => {
  if (!row || typeof row !== 'object') return null;
  const value = row[field];
  const normalized = normalizeDisplayValue(value);
  return normalized === 'Não informado' ? null : value;
};

export const buildFilterLabel = (field, value) => {
  const fieldLabel = FIELD_LABELS[field] || field;
  const rawValue = value === null || value === undefined || String(value).trim() === ''
    ? 'Não informado'
    : String(value);
  const displayValue = ['ano_mes_abertura', 'ano_mes_fechamento'].includes(field)
    ? formatInfraMonth(rawValue)
    : rawValue;
  return `${fieldLabel}: ${displayValue}`;
};

export const sameFilter = (a, b) => (
  a?.field === b?.field
  && String(a?.value ?? '') === String(b?.value ?? '')
);

export const applyInfraFilters = (rows, filters) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  if (!Array.isArray(filters) || filters.length === 0) {
    return rows;
  }

  const filtersByField = filters.reduce((acc, filter) => {
    if (!filter?.field) return acc;
    if (!acc[filter.field]) acc[filter.field] = [];
    acc[filter.field].push(filter);
    return acc;
  }, {});

  return rows.filter((row) => (
    Object.entries(filtersByField).every(([field, fieldFilters]) => {
      const rowValue = getRowValue(row, field);
      const normalizedRowValue = rowValue === null || rowValue === undefined || String(rowValue).trim() === ''
        ? null
        : rowValue;

      return fieldFilters.some((filter) => {
        if (filter.value === null) {
          return normalizedRowValue === null;
        }

        return normalizeText(normalizedRowValue) === normalizeText(filter.value);
      });
    })
  ));
};

export const distinctRowsBy = (rows, field) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = String(row?.[field] ?? '').trim();
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const groupCount = (rows, field, { distinctBy } = {}) => {
  const map = new Map();
  const processed = Array.isArray(rows) ? rows : [];

  processed.forEach((row, index) => {
    const label = normalizeDisplayValue(getRowValue(row, field));
    const key = label;
    if (!map.has(key)) {
      map.set(key, { label, count: 0, sum: 0, rows: [], index });
    }
    const bucket = map.get(key);
    bucket.count += 1;
    bucket.rows.push(row);
  });

  const result = Array.from(map.values()).map((bucket) => {
    if (!distinctBy) {
      return { label: bucket.label, count: bucket.count };
    }

    const seen = new Set();
    const distinctCount = bucket.rows.reduce((acc, row) => {
      const key = String(row?.[distinctBy] ?? '').trim();
      if (!key || seen.has(key)) return acc;
      seen.add(key);
      return acc + 1;
    }, 0);

    return { label: bucket.label, count: distinctCount };
  });

  return result.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'pt-BR'));
};

export const groupSum = (rows, groupField, sumField) => {
  const map = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const label = normalizeDisplayValue(getRowValue(row, groupField));
    const current = map.get(label) || 0;
    map.set(label, current + normalizeNumber(row?.[sumField]));
  });

  return Array.from(map.entries())
    .map(([label, total]) => ({ label, total }))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label, 'pt-BR'));
};

export const groupAverage = (rows, groupField, valueField) => {
  const map = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const label = normalizeDisplayValue(getRowValue(row, groupField));
    const value = normalizeNumber(row?.[valueField]);
    if (!Number.isFinite(value)) return;
    const bucket = map.get(label) || { sum: 0, count: 0 };
    bucket.sum += value;
    bucket.count += 1;
    map.set(label, bucket);
  });

  return Array.from(map.entries())
    .map(([label, bucket]) => ({
      label,
      total: bucket.count > 0 ? bucket.sum / bucket.count : 0,
    }))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label, 'pt-BR'));
};

export const groupByMonth = (rows, valueField = null) => {
  const map = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const month = getInfraHistoryMonth(row, 'ano_mes_fechamento');
    if (month === 'Não informado') return;
    const bucket = map.get(month) || { label: formatInfraMonth(month), rawMonth: month, count: 0, total: 0 };
    bucket.count += 1;
    if (valueField) {
      bucket.total += normalizeNumber(row?.[valueField]);
    }
    map.set(month, bucket);
  });

  return Array.from(map.values()).sort((a, b) => a.rawMonth.localeCompare(b.rawMonth, 'pt-BR'));
};
